import { Component, Inject } from "@angular/core"
import { CommonModule } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { MatDialogRef, MatDialogModule, MAT_DIALOG_DATA } from "@angular/material/dialog"
import { MatButtonModule } from "@angular/material/button"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatInputModule } from "@angular/material/input"
import { MatSelectModule } from "@angular/material/select"
import { MatCheckboxModule } from "@angular/material/checkbox"
import { SnackbarService } from "../../../services/snackbar.service"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { MatIconModule } from "@angular/material/icon"
import { MatRadioModule } from "@angular/material/radio"

import {
  StationRatesService,
  CreateStationRateRequest,
  BulkCreateStationRatesRequest,
} from "../../../services/station-rates.service"
import { Station } from "../../../models/station.model"
import { NoScrollInputDirective } from "../../../directives/no-scroll-input.directive"
import { formatRateDayRuns } from "../../../utils/rate-days"

export interface DialogData {
  station: Station
}

@Component({
  selector: "app-bulk-upload-dialog",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatRadioModule,
    NoScrollInputDirective,
  ],
  templateUrl: "./bulk-upload-dialog.component.html",
  styleUrls: ["./bulk-upload-dialog.component.css"],
})
export class BulkUploadDialogComponent {
  loading = false
  manualRates: CreateStationRateRequest[] = []

  // Template for new manual rate
  newRate: CreateStationRateRequest = {
    station_id: 0,
    spot_length: 30,
    start_hour: 6,
    end_hour: 18,
    days: {
      mon: true,
      tue: true,
      wed: true,
      thu: true,
      fri: true,
      sat: false,
      sun: false,
    },
    cpm: 5.0,
    pricing_type: "cpm",
  }

  spotLengthOptions = [
    { value: 15, label: "15s" },
    { value: 30, label: "30s" },
    { value: 60, label: "60s" },
  ]

  hourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: this.formatHour(i),
  }))

  /**
   * end_hour is INCLUSIVE on the backend (18 = the 6:00–6:59 PM hour; the rates table shows
   * it as ending 7 PM), so each option names the whole hour it covers. Stored values are unchanged.
   */
  endHourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: `${this.formatHour(i)}-${this.formatHour((i + 1) % 24)}`,
  }))

  constructor(
    private dialogRef: MatDialogRef<BulkUploadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private stationRatesService: StationRatesService,
    private snackbar: SnackbarService,
  ) {
    this.newRate.station_id = this.data.station.id
  }

  formatHour(hour: number): string {
    if (hour === 0) return "12a"
    if (hour < 12) return `${hour}a`
    if (hour === 12) return "12p"
    return `${hour - 12}p`
  }

  addManualRate(): void {
    if (!this.isRateValid(this.newRate)) {
      this.snackbar.warning("Please fill in all required fields for the new rate", 3000)
      return
    }

    this.manualRates.push({ ...this.newRate })

    // Reset form
    this.newRate = {
      station_id: this.data.station.id,
      spot_length: 30,
      start_hour: 6,
      end_hour: 18,
      days: {
        mon: true,
        tue: true,
        wed: true,
        thu: true,
        fri: true,
        sat: false,
        sun: false,
      },
      cpm: 5.0,
      pricing_type: "cpm",
    }
  }

  removeManualRate(index: number): void {
    this.manualRates.splice(index, 1)
  }

  isRateValid(rate: CreateStationRateRequest): boolean {
    const hasValidPricing = rate.cpm !== undefined && rate.cpm > 0
    const hasSelectedDays = Object.values(rate.days).some((day) => day)
    const hasValidTimeRange = rate.end_hour >= rate.start_hour
    return hasValidPricing && hasSelectedDays && hasValidTimeRange
  }

  isFormValid(): boolean {
    return this.manualRates.length > 0 && this.manualRates.every((rate) => this.isRateValid(rate))
  }

  formatDays(days: CreateStationRateRequest["days"]): string {
    // "M-W" is a Mon–Wed range; non-adjacent days are comma-separated ("M, W").
    return formatRateDayRuns(days, ["M", "T", "W", "Th", "F", "S", "Su"])
  }

  onSubmit(): void {
    if (!this.isFormValid()) {
      this.snackbar.warning("Please provide valid rate data", 3000)
      return
    }

    this.loading = true

    const bulkData: BulkCreateStationRatesRequest = {
      rates: this.manualRates.map((rate) => ({
        ...rate,
        pricing_type: rate.pricing_type || "cpm",
      })),
    }

    this.stationRatesService.createBulkStationRates(bulkData).subscribe({
      next: (response) => {
        this.snackbar.success(`Successfully created ${response.created_entries.length} station rates`, 3000)
        this.dialogRef.close(true)
      },
      error: (error) => {
        console.error("Error creating bulk station rates:", error)
        const errorMessage = error.error?.error || "Error creating station rates"
        this.snackbar.error(errorMessage, 5000)
        this.loading = false
      },
    })
  }

  onCancel(): void {
    this.dialogRef.close(false)
  }
}
